import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { formatErr, gbp, http, imgUrl, typeLabel } from "../lib/api";
import { LogoMark } from "../components/site/Logo";
import { ProductForm } from "../components/admin/ProductForm";

const list = (r) => (Array.isArray(r?.data) ? r.data : []);
const when = (s) => (s ? new Date(s).toLocaleString("en-GB") : "");

export default function Admin() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [products, setProducts] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(undefined);

  const load = useCallback(async () => {
    const [p, e, o] = await Promise.all([http.get("/products"), http.get("/enquiries"), http.get("/orders")]);
    setProducts(list(p));
    setEnquiries(list(e));
    setOrders(list(o));
  }, []);

  useEffect(() => {
    http.get("/auth/me").then((r) => { setMe(r.data); load().catch(() => {}); }).catch(() => nav("/admin/login"));
  }, [nav, load]);

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await http.delete(`/products/${p.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const logout = async () => { await http.post("/auth/logout").catch(() => {}); nav("/admin/login"); };

  if (!me) return <main className="min-h-screen" data-testid="admin-loading" />;

  return (
    <main data-testid="admin-page" className="mx-auto max-w-6xl px-4 py-8 sm:px-8">
      <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3"><LogoMark /><span className="font-display text-2xl font-extrabold uppercase">Admin</span></Link>
        <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"><LogOut className="h-4 w-4" /> Log out</button>
      </div>
      <Tabs defaultValue="products" className="mt-8">
        <TabsList className="bg-[#121820]">
          <TabsTrigger value="products" data-testid="admin-tab-products">Airbags ({products.length})</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders ({orders.filter((o) => o.payment_status === "paid").length})</TabsTrigger>
        </TabsList>
        <TabsContent value="products" className="mt-6">
          <button data-testid="admin-add-product-button" onClick={() => setEditing(null)} className="mb-6 flex h-11 items-center gap-2 rounded-full bg-[#E11D48] px-5 text-sm font-semibold hover:bg-[#F43F5E]"><Plus className="h-4 w-4" /> Add airbag</button>
          <div className="divide-y divide-white/10 border border-white/10">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex items-center gap-4 p-3">
                <img src={imgUrl(p.images?.[0])} alt="" className="h-14 w-14 shrink-0 object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{p.title}</p>
                  <p className="font-mono text-xs text-slate-400">{typeLabel(p.airbag_type)} · {gbp(p.price)} · stock {p.stock}{p.featured ? " · featured" : ""}</p>
                </div>
                <button data-testid={`admin-edit-${p.id}`} onClick={() => setEditing(p)} className="rounded-full border border-white/15 p-2 hover:bg-white/10" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`admin-delete-${p.id}`} onClick={() => del(p)} className="rounded-full border border-white/15 p-2 hover:border-[#E11D48] hover:text-[#F43F5E]" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {products.length === 0 && <p className="p-8 text-center text-slate-400">No airbags yet. Add your first one.</p>}
          </div>
        </TabsContent>
        <TabsContent value="enquiries" className="mt-6 space-y-3">
          {enquiries.length === 0 && <p className="text-slate-400">No enquiries yet.</p>}
          {enquiries.map((e) => (
            <div key={e.id} data-testid={`admin-enquiry-${e.id}`} className="border border-white/10 p-4">
              <div className="flex flex-wrap justify-between gap-2"><p className="font-semibold">{e.name}</p><p className="font-mono text-xs text-slate-400">{when(e.created_at)}</p></div>
              <p className="mt-1 font-mono text-xs text-slate-300">{[e.phone, e.email].filter(Boolean).join(" · ")}</p>
              {e.product_title && <p className="mt-2 text-xs text-[#F43F5E]">Re: {e.product_title}</p>}
              <p className="mt-2 whitespace-pre-line text-sm text-slate-200">{e.message}</p>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="orders" className="mt-6 space-y-3">
          {orders.length === 0 && <p className="text-slate-400">No orders yet.</p>}
          {orders.map((o) => (
            <div key={o.session_id} className="border border-white/10 p-4">
              <div className="flex flex-wrap justify-between gap-2">
                <p className="font-semibold">{gbp(o.amount_paid || o.amount)}{o.shipping_paid ? <span className="ml-2 font-mono text-xs text-slate-400">incl. {gbp(o.shipping_paid)} delivery</span> : null} <span className={`ml-2 font-mono text-xs uppercase ${o.payment_status === "paid" ? "text-emerald-400" : "text-slate-400"}`}>{o.payment_status}</span></p>
                <p className="font-mono text-xs text-slate-400">{when(o.created_at)}</p>
              </div>
              <p className="mt-1 font-mono text-xs text-slate-300">{[o.customer_name, o.customer_email, o.customer_phone].filter(Boolean).join(" · ")}</p>
              <ul className="mt-2 text-sm text-slate-200">{(o.items || []).map((i) => <li key={i.product_id}>{i.quantity} × {i.title}</li>)}</ul>
            </div>
          ))}
        </TabsContent>
      </Tabs>
      <Dialog open={editing !== undefined} onOpenChange={(o) => !o && setEditing(undefined)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#0D1218] sm:max-w-xl" data-lenis-prevent>
          <DialogHeader><DialogTitle className="font-display text-3xl font-extrabold uppercase">{editing ? "Edit airbag" : "Add airbag"}</DialogTitle></DialogHeader>
          {editing !== undefined && <ProductForm key={editing?.id || "new"} product={editing} onSaved={() => { setEditing(undefined); load(); }} />}
        </DialogContent>
      </Dialog>
    </main>
  );
}
